import * as React from "react";
import AuthContext from "../../context/AuthProvider";
import { useLocation } from "react-router-dom";
import { useState, useContext, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Button, ButtonGroup } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Chat } from "@progress/kendo-react-conversational-ui";
import { AppBar, AppBarSection, Card } from "@progress/kendo-react-layout";
import { Popup } from "@progress/kendo-react-popup";
import { BsThreeDotsVertical } from "react-icons/bs";
import clientIcon from "../../assets/clientIcon.png";
import supervisorIcon from "../../assets/supervisorIcon.png";
import calendarIcon from "../../assets/calendar.png";
import taskIcon from "../../assets/task.png";
import "./dashboard.css";
import { useFetchData } from "./hooks/useFetchData";
import UserScreen from "../UserScreen/UserScreen";
import newTabIcon from "../../assets/newTabIcon.png";
import { formatDateLong, formatDateShort } from "../../utils/dateUtils";

const user = {
  id: 1,
};

const Dashboard = () => {
  const { setAuth } = useContext(AuthContext);
  const navigate = useNavigate();
  const anchor = useRef(null);
  const [show, setShow] = useState(false);
  const [view, setView] = useState("messages");
  const [dialogVisible, setDialogVisible] = useState(false);
  const [iframeUrl, setIframeUrl] = useState("");
  const location = useLocation();
  const [showUserScreen, setShowUserScreen] = useState(false);
  const [userScreenId, setUserScreenId] = useState(null);
  const [taskType, setTaskType] = useState("");
  const defaultMatterId = location.state?.matterId;

  const {
    matters,
    matterInfo,
    tasks,
    messages,
    selectedMatter,
    setSelectedMatter,
    fetchTasks,
    fetchMessages,
    addNewMessage,
  } = useFetchData();

  const handleMessage = useCallback((event) => {
    if (event.data && event.data.status === "completed") {
      setDialogVisible(false);
      setIframeUrl("");
    }
  }, []);

  useEffect(() => {
    if (!selectedMatter) {
      const defaultMatter = matters?.find(
        (matter) => matter.matterId === defaultMatterId
      );
      const openMatter = matters?.find((matter) => matter.status === "Open");
      setSelectedMatter(defaultMatter || openMatter || matters[0]);
    }
  }, [matters, selectedMatter, location.state?.matterId]);

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [handleMessage]);

  const MatterData = [
    {
      title: "Solicitor",
      name: matterInfo?.feeEarnerName,
      avatar: clientIcon,
      color: "#E4EAFF",
    },
    {
      title: "Supervisor",
      name: matterInfo?.supervisorName,
      avatar: supervisorIcon,
      color: "#E4F7FF",
    },
    {
      title: "Date Opened",
      name: matterInfo ? formatDateLong(matterInfo?.startDate) : "",
      avatar: calendarIcon,
      color: "#FBE9FF",
    },

    {
      title: "Status",
      name: matterInfo?.status,
      avatar: taskIcon,
      color: "#FFE2C5",
    },
  ];

  const title =
    view === "tasks"
      ? "Tasks"
      : view === "messages"
      ? "Messages"
      : "Notifications";

  const handleOpenDialog = (url) => {
    setIframeUrl(url);
    setDialogVisible(true);
  };

  const handleCloseDialog = () => {
    setDialogVisible(false);
    setIframeUrl("");
  };

  const handleViewChange = (newView) => {
    setView(newView);
  };

  const handleClick = () => {
    setShow(!show);
  };

  const logout = async () => {
    setAuth({});
    localStorage.removeItem("accessToken");
    navigate("/");
  };

  const handleMatterChange = (e) => {
    const selected = e.target.value;
    setSelectedMatter(selected);
  };

  const handleTaskClick = (item) => {
    if (item.type === "DataCollection") {
      setShowUserScreen(true);
      setUserScreenId(item.userScreenId);
    } else if (item.type === "IDVerification") {
      handleOpenDialog(item.webURL);
      setTaskType("IDVerification");
    } else {
      handleOpenDialog(
        "https://docassemble.linq.legal/interview?i=docassemble.playground1:ClientInformation.yml#page1"
      );
    }
  };

  const DialogTitleBar = (titleText) => {
    return (
      <div className="w-100 d-flex align-items-center justify-content-between">
        <div>{titleText}</div>
        {taskType === "IDVerification" && (
          <div className="d-flex align-items-center mx-2 ">
            <Button className="border-0 cursor-pointer">
              <img
                src={newTabIcon}
                onClick={() => {
                  window.open(iframeUrl, "_blank");
                }}
                alt="new tab"
                width={16}
                height={16}
              />
            </Button>
          </div>
        )}
      </div>
    );
  };

  const valueRender = (element, value) => {
    if (!value) {
      return element;
    }
    const children = [
      <div key={value?.matterRef} className="dropdown-box">
        <span className="f-sm mx-1 mx-md-auto fw-semibold text-wrap">
          Case Reference:
        </span>
        <span
          className="mx-md-auto mx-1 f-md fw-bold"
          style={{
            color: "#2057FF",
          }}
        >
          {value?.matterRef || "Select Matter"}
        </span>
      </div>,
    ];
    return React.cloneElement(
      element,
      {
        ...element.props,
      },
      children
    );
  };

  const handleRefresh = () => {
    if (view === "messages") {
      fetchMessages(selectedMatter?.matterId);
    } else if (view === "tasks") {
      fetchTasks(selectedMatter?.matterId);
    }
    if (view === "notifications") {
      // fetchNotifications();
    }
  };

  return (
    <div className="background-container vh-100 ">
      <AppBar className="px-md-5 px-sm-4 px-xs-2 appbar">
        <AppBarSection className="appbar-title">
          <h1 className="title">Solicitors</h1>
        </AppBarSection>

        <AppBarSection className="appbar-avatar">
          <div className="nav-avatar-container ">
            <img src={clientIcon} width={30} height={30} alt="Avatar" />
          </div>
        </AppBarSection>

        <AppBarSection className="appbar-avatar-menu">
          <div ref={anchor}>
            <div
              className="mx-2 nav-avatar-container"
              onClick={handleClick}
              type="button"
            >
              <BsThreeDotsVertical size={20} color="black" />
            </div>
          </div>
          {show && (
            <Popup
              anchor={anchor.current}
              show={show}
              style={{
                marginLeft: -10,
                backgroundColor: "#EBEBEB",
              }}
            >
              <div className="content">
                <div>
                  <span>My Profile</span>
                </div>
                <hr className="w-100 my-2" />
                <div onClick={logout}>
                  <span>Log Out</span>
                </div>
              </div>
            </Popup>
          )}
        </AppBarSection>
      </AppBar>
      <Card className="px-4 py-3 mt-3 main-card">
        <div className="d-flex justify-content-between align-items-center">
          <span className="fw-bold f-lg me-3">
            {(selectedMatter && selectedMatter?.details) ||
              (matters && matters[0]?.details)}
          </span>
          <DropDownList
            key={selectedMatter?.matterId}
            className="pe-2 rounded-2 dropdown-list"
            data={matters}
            defaultValue={selectedMatter}
            onChange={handleMatterChange}
            valueRender={valueRender}
            textField="matterRef"
          />
        </div>
        <hr className="my-2" />

        <div className="container-fluid px-md-0">
          <div className="row d-md-none d-flex ">
            <Card
              className="p-2 rounded"
              style={{
                backgroundColor: "#E4EAFF",
              }}
            >
              <div className="d-flex flex-column mx-2">
                {MatterData.map((item, index) => (
                  <div
                    key={index}
                    className="d-flex me-2 justify-content-start"
                  >
                    <span className="mb-1 f-sm fw-bold mx-2">
                      {item.title}:
                    </span>
                    <span className="fw-semibold f-md">{item.name}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          <div className="row d-none d-md-flex ">
            {MatterData &&
              MatterData?.map((data, index) => (
                <div className="col-6 col-xl-3">
                  <Card
                    key={index}
                    className="details-card my-md-2"
                    style={{
                      backgroundColor: data.color,
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between mx-2">
                      <div className="d-flex flex-column me-2 justify-content-start">
                        <span className="mb-1 f-sm fw-bold">{data.title}:</span>
                        <span className="fw-semibold f-md">
                          {data.name || ""}
                        </span>
                      </div>
                      <div className="avatar-container">
                        <img width={35} height={35} src={data.avatar} />
                      </div>
                    </div>
                  </Card>
                </div>
              ))}
          </div>
        </div>
        <div className="mt-3 button-group">
          <ButtonGroup width="100%">
            <Button
              className="f-md rounded-pill border-0 ms-1"
              style={{
                padding: "6px",
                backgroundColor: view === "tasks" ? "#2057FF" : "white",
                color: view === "tasks" ? "white" : "black",
              }}
              onClick={() => {
                handleViewChange("tasks");
              }}
            >
              Tasks
            </Button>
            <Button
              className="f-md rounded-pill border-0 mx-1"
              style={{
                backgroundColor: view === "messages" ? "#2057FF" : "white",
                color: view === "messages" ? "white" : "black",
              }}
              onClick={() => handleViewChange("messages")}
            >
              Messages
            </Button>
            <Button
              className="f-md rounded-pill border-0 me-1"
              style={{
                backgroundColor: view === "notifications" ? "#2057FF" : "white",
                color: view === "notifications" ? "white" : "black",
              }}
              onClick={() => handleViewChange("notifications")}
            >
              Notifications
            </Button>
          </ButtonGroup>
        </div>
        <div className="mt-3 item-container">
          <div className=" d-flex justify-content-between align-items-center">
            <span className="fw-semibold f-lg">{title}</span>
            <span
              className="k-icon k-font-icon k-i-reload p-3 border rounded-circle"
              onClick={handleRefresh}
              style={{ cursor: "pointer", backgroundColor: "#f9f9fc" }}
            ></span>
          </div>

          <hr className="my-2" />
          {view === "tasks" &&
            tasks.map((item, index) => (
              <div
                key={index}
                className="task-item d-flex align-items-center px-3"
              >
                <span className="me-2 fw-semibold">{item.details}</span>
                <div className="ms-auto d-flex align-items-center">
                  {item.dateCompleted && (
                    <span className="me-4 d-none d-md-flex f-sm text-secondary">
                      {formatDateShort(item.dateCompleted)}
                    </span>
                  )}
                  <Button
                    className="update-button"
                    onClick={() => handleTaskClick(item)}
                    disabled={item.status === "Completed"}
                  >
                    {item.status}
                  </Button>
                </div>
              </div>
            ))}
          {view === "messages" && selectedMatter && (
            <Chat
              className="chat-box"
              user={user}
              messages={messages}
              onMessageSend={addNewMessage}
              placeholder={"Type a message..."}
            />
          )}
          {view === "notifications" && (
            <div className="task-item fw-semibold px-3">
              <span className="me-2">Notification 1: Check your email</span>
              <Button className="update-button">Open</Button>
            </div>
          )}
        </div>
      </Card>
      {dialogVisible && (
        <Dialog
          onClose={handleCloseDialog}
          className="dialog-md"
          title={DialogTitleBar("Client Information")}
        >
          <iframe
            id="TaskExample"
            title="Task Example"
            width="100%"
            height="98%"
            src={iframeUrl}
          ></iframe>
        </Dialog>
      )}

      {showUserScreen && userScreenId !== null && (
        <UserScreen
          InterviewId={userScreenId}
          matterId={selectedMatter?.matterId}
          onClose={() => {
            setShowUserScreen(false);
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;
